import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Expression, ExpressionsByLevel, SongDetailsAPI } from "../../api/songDetails";
import useLyrics from "../../hooks/useLyrics";
import { CurrentlyPlayingState } from "../../types/currentlyPlayingState";
import { useBackgroundFetch } from "../../context/BackgroundFetchContext";
import config from "../../config";

interface CuriositiesPageProps {
  currentlyPlayingSong: CurrentlyPlayingState;
}

const CuriositiesPage: React.FC<CuriositiesPageProps> = ({ currentlyPlayingSong }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [songDetails, setSongDetails] = useState<{
    songDetailMd: string;
    expressions: ExpressionsByLevel;
  } | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<"beginner" | "intermediate" | "advanced">(
    "advanced"
  );

  // Use refs to prevent multiple API calls for the same song
  const lastProcessedSong = useRef<string>("");
  const lastProcessedSongId = useRef<string>("");
  const isProcessing = useRef(false);

  const location = useLocation();
  const navigate = useNavigate();

  // Use the existing lyrics hook to get real lyrics
  const lyrics = useLyrics(currentlyPlayingSong);

  // Use background fetch context to check if data is already being fetched
  const backgroundFetch = useBackgroundFetch();

  useEffect(() => {
    const loadSongDetails = async () => {
      if (!currentlyPlayingSong?.currentlyPlayingObject?.item) {
        setError("No song currently playing");
        return;
      }

      const song = currentlyPlayingSong.currentlyPlayingObject.item;
      const artist = song.artists.map(a => a.name).join(", ");
      const title = song.name;
      const album = song.album.name;
      const spotifyId = song.id;

      // Create a unique song identifier
      const songKey = `${artist}-${title}-${album}`;

      // If song has changed (by ID), reset the state and clear previous data
      if (lastProcessedSongId.current !== "" && lastProcessedSongId.current !== song.id) {
        setSongDetails(null);
        setError(null);
        lastProcessedSong.current = "";
        lastProcessedSongId.current = "";
        isProcessing.current = false;
      }

      // Prevent multiple API calls for the same song
      if (lastProcessedSong.current === songKey || isProcessing.current) {
        return;
      }

      // Prevent multiple simultaneous API calls
      if (isProcessing.current) {
        return;
      }

      // Wait for lyrics to be available AND ensure they match the current song
      if (!lyrics?.lyrics?.plainLyrics) {
        return; // Don't proceed until lyrics are loaded
      }

      // Check if the lyrics are for the current song by comparing the song ID
      if (lyrics.currentlyPlayingItemId !== song.id) {
        return; // Wait for lyrics to match the current song
      }

      // Check if background fetch is already in progress for this song
      if (backgroundFetch.isFetching && backgroundFetch.currentSongId === song.id) {
        setLoading(true);
        setError(null);
        return; // Let background fetch handle it
      }

      // Check if background fetch already has data for this song
      if (backgroundFetch.hasData && backgroundFetch.currentSongId === song.id) {
        // Try to get the data that was already fetched
        try {
          const response = await fetch(
            `${config.api.root}/api/songdetail?artist=${encodeURIComponent(artist)}&title=${encodeURIComponent(title)}&level=all`
          );

          if (response.ok) {
            const data = await response.json();
            setSongDetails(data);
            setLoading(false);
            setError(null);
            return;
          }
        } catch (err) {
          console.error("Error getting background fetched data:", err);
        }
      }

      isProcessing.current = true;
      lastProcessedSong.current = songKey;
      lastProcessedSongId.current = song.id;

      setLoading(true);
      setError(null);

      try {
        // Use the actual lyrics from the useLyrics hook
        const actualLyrics = lyrics.lyrics.plainLyrics;

        const details = await SongDetailsAPI.getSongDetails(
          artist,
          title,
          "advanced", // Using advanced level as requested
          actualLyrics,
          album,
          spotifyId
        );

        // Validate that we have expressions before setting state
        if (
          details &&
          details.expressions &&
          (details.expressions.advanced?.length > 0 ||
            details.expressions.intermediate?.length > 0 ||
            details.expressions.beginner?.length > 0)
        ) {
          setSongDetails(details);

          // Auto-select the first available level
          if (details.expressions.advanced?.length > 0) {
            setSelectedLevel("advanced");
          } else if (details.expressions.intermediate?.length > 0) {
            setSelectedLevel("intermediate");
          } else if (details.expressions.beginner?.length > 0) {
            setSelectedLevel("beginner");
          }
        } else {
          setError("No expressions found for this song");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load song details");
      } finally {
        setLoading(false);
        isProcessing.current = false;
      }
    };

    loadSongDetails();
  }, [currentlyPlayingSong, lyrics, backgroundFetch]);

  // Show background fetch loading state
  if (backgroundFetch.isFetching && !songDetails) {
    return (
      <Box sx={{ mt: { xs: 2, sm: 4 } }}>
        <Typography variant="h4" gutterBottom>
          Song Curiosities
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body1">
            Analyzing song lyrics and preparing curiosities...
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This may take a few moments for new songs.
        </Typography>
      </Box>
    );
  }

  const renderMarkdown = (markdown: string) => {
    // Improved markdown rendering
    return markdown
      .split("\n")
      .map((line, index) => {
        // Handle headers (###, ##, #)
        if (line.startsWith("### ")) {
          return (
            <Typography key={index} variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
              {line.replace("### ", "").replace(/:$/, "")}
            </Typography>
          );
        }
        if (line.startsWith("## ")) {
          return (
            <Typography
              key={index}
              variant="h5"
              gutterBottom
              sx={{
                mt: 2,
                mb: 1,
                color: "success.main", // Green color for section headers
                fontWeight: "bold"
              }}
            >
              {line.replace("## ", "").replace(/:$/, "")}
            </Typography>
          );
        }
        if (line.startsWith("# ")) {
          return (
            <Typography key={index} variant="h4" gutterBottom sx={{ mt: 2, mb: 1 }}>
              {line.replace("# ", "").replace(/:$/, "")}
            </Typography>
          );
        }

        // Handle numbered lists with bold text
        if (line.match(/^\d+\.\s+\*\*(.*?)\*\*:\s*(.*)/)) {
          const match = line.match(/^\d+\.\s+\*\*(.*?)\*\*:\s*(.*)/);
          if (match) {
            return (
              <Box key={index} sx={{ mb: 1, pl: 2 }}>
                <Typography component="span" variant="body1" fontWeight="bold" color="primary">
                  {match[1]}:
                </Typography>
                <Typography component="span" variant="body1" sx={{ ml: 1 }}>
                  {match[2]}
                </Typography>
              </Box>
            );
          }
        }

        // Handle bullet points with bold text
        if (line.startsWith("- **")) {
          const match = line.match(/-\s+\*\*(.*?)\*\*:\s*(.*)/);
          if (match) {
            return (
              <Box key={index} sx={{ mb: 1, pl: 2 }}>
                <Typography component="span" variant="body1" fontWeight="bold" color="primary">
                  {match[1]}:
                </Typography>
                <Typography component="span" variant="body1" sx={{ ml: 1 }}>
                  {match[2]}
                </Typography>
              </Box>
            );
          }
        }

        // Handle regular text with bold formatting
        if (line.includes("**")) {
          const parts = line.split(/(\*\*.*?\*\*)/g);
          return (
            <Typography key={index} variant="body1" paragraph>
              {parts.map((part, partIndex) => {
                if (part.startsWith("**") && part.endsWith("**")) {
                  return (
                    <Typography
                      key={partIndex}
                      component="span"
                      variant="body1"
                      fontWeight="bold"
                      color="primary"
                    >
                      {part.slice(2, -2)}
                    </Typography>
                  );
                }
                return part;
              })}
            </Typography>
          );
        }

        // Handle regular lines
        if (line.trim()) {
          return (
            <Typography key={index} variant="body1" paragraph>
              {line}
            </Typography>
          );
        }

        return null;
      })
      .filter(Boolean);
  };

  if (loading) {
    return (
      <Box sx={{ mt: { xs: 2, sm: 4 }, textAlign: "center" }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Analyzing song details...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: { xs: 2, sm: 4 } }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Unable to load song details. Please try again later.
        </Typography>
        {error === "No expressions found for this song" && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              This song might not have enough challenging expressions to analyze. Try playing a
              different song with more complex lyrics.
            </Typography>
          </Alert>
        )}
      </Box>
    );
  }

  if (!lyrics?.lyrics?.plainLyrics) {
    return (
      <Box sx={{ mt: { xs: 2, sm: 4 } }}>
        <Typography variant="h4" gutterBottom>
          Song Curiosities
        </Typography>
        <Typography variant="body1">Loading lyrics for the current song...</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          If lyrics are not available for this song, the curiosities feature will be disabled.
        </Typography>
      </Box>
    );
  }

  if (!songDetails) {
    return (
      <Box sx={{ mt: { xs: 2, sm: 4 } }}>
        <Typography variant="h4" gutterBottom>
          Song Curiosities
        </Typography>
        <Typography variant="body1">
          No song details available. Please play a song to see its curiosities.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 } }}>
      <Typography variant="h4" gutterBottom>
        Song Curiosities
      </Typography>

      {/* Song Details Section */}
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 } }}>
        <Typography variant="h5" gutterBottom>
          Song Facts & Background
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box>{renderMarkdown(songDetails.songDetailMd)}</Box>
      </Paper>

      {/* Expressions Section - More Compact Layout */}
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            justifyContent: "space-between",
            mb: 2
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
            Expressions
          </Typography>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Level</InputLabel>
            <Select
              value={selectedLevel}
              label="Level"
              onChange={e =>
                setSelectedLevel(e.target.value as "beginner" | "intermediate" | "advanced")
              }
            >
              <MenuItem value="beginner">Beginner</MenuItem>
              <MenuItem value="intermediate">Intermediate</MenuItem>
              <MenuItem value="advanced">Advanced</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Show message if no expressions at all */}
        {!songDetails.expressions.advanced?.length &&
          !songDetails.expressions.intermediate?.length &&
          !songDetails.expressions.beginner?.length && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                This song doesn't have enough challenging expressions to analyze. Try a different
                song with more complex lyrics.
              </Typography>
            </Alert>
          )}
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          {songDetails.expressions[selectedLevel]?.length > 0 ? (
            songDetails.expressions[selectedLevel].map((expression: Expression, index: number) => (
              <Grid item xs={12} md={6} key={index}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1, flexWrap: "wrap" }}>
                      <Typography
                        variant="subtitle1"
                        component="span"
                        sx={{
                          mr: 1,
                          fontWeight: "bold",
                          color: "primary.main"
                        }}
                      >
                        {expression.expression}
                      </Typography>
                      <Chip
                        label={expression.difficulty}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: "0.7rem" }}
                      />
                    </Box>

                    <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 1 }}>
                      <strong>Meaning:</strong> {expression.meaning}
                    </Typography>

                    {expression.example && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontStyle: "italic" }}
                      >
                        <strong>Example:</strong> "{expression.example}"
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Box sx={{ textAlign: "center", py: 3 }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  No {selectedLevel} level expressions found for this song.
                </Typography>

                {/* Show available levels */}
                {songDetails.expressions && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Available levels:
                    </Typography>
                    <Box
                      sx={{ display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap" }}
                    >
                      {songDetails.expressions.advanced?.length > 0 && (
                        <Chip
                          label={`Advanced (${songDetails.expressions.advanced.length})`}
                          color="primary"
                          variant="outlined"
                          size="small"
                          onClick={() => setSelectedLevel("advanced")}
                          sx={{ cursor: "pointer" }}
                        />
                      )}
                      {songDetails.expressions.intermediate?.length > 0 && (
                        <Chip
                          label={`Intermediate (${songDetails.expressions.intermediate.length})`}
                          color="primary"
                          variant="outlined"
                          size="small"
                          onClick={() => setSelectedLevel("intermediate")}
                          sx={{ cursor: "pointer" }}
                        />
                      )}
                      {songDetails.expressions.beginner?.length > 0 && (
                        <Chip
                          label={`Beginner (${songDetails.expressions.beginner.length})`}
                          color="primary"
                          variant="outlined"
                          size="small"
                          onClick={() => setSelectedLevel("beginner")}
                          sx={{ cursor: "pointer" }}
                        />
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  );
};

export default CuriositiesPage;
